import { BehaviorSubject } from 'rxjs';
import { AccountInfo } from '@azure/msal-browser';

import { apiService } from './api';
import { msalInstance } from '../config/auth';
import { environment } from '../config/environment';
import { User, UserRole, AuthResponse } from '../types/common.types';

export type { UserRole };

/**
 * Authentication service for Azure AD B2B authentication via MSAL
 * Manages user authentication, token acquisition, role-based access control, and session management
 * 
 * Token refresh is handled automatically by MSAL's acquireTokenSilent method.
 * Idle timeout is managed by react-idle-timer component.
 */
class AuthService {
  private userSubject = new BehaviorSubject<User | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private authenticatedSubject = new BehaviorSubject<boolean>(false);
  private accessDeniedSubject = new BehaviorSubject<boolean>(false);

  /**
   * Checks if user is authenticated with valid token
   * Verifies token expiration and attempts silent token acquisition
   * @returns {Promise<boolean>} True if authenticated, false otherwise
   */
  async checkAuthentication(): Promise<boolean> {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      this.authenticatedSubject.next(false);
      return false;
    }

    const account = accounts[0];
    const request = {
      scopes: environment.azure.scopes,
      account
    };

    try {
      await msalInstance.acquireTokenSilent(request);
      this.authenticatedSubject.next(true);
      return true;
    } catch (error) {
      this.authenticatedSubject.next(false);
      return false;
    }
  }

  /**
   * Validates user with backend API and retrieves user data
   * Checks for role changes and clears cache if role has changed
   * @returns {Promise<AuthResponse>} Backend validation response with user data
   */
  async validateUserWithBackend(): Promise<AuthResponse> {
    this.loadingSubject.next(true);

    const accounts = msalInstance.getAllAccounts();
    const request = {
      scopes: environment.azure.scopes,
      account: accounts[0]
    };

    try {
      const response = await msalInstance.acquireTokenSilent(request);
      
      const authResponse = await apiService.post<AuthResponse>('/auth/validate', {}, {
        headers: {
          'Authorization': `Bearer ${response.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (authResponse.user) {
        // Check if role changed - if so, clear cache and redirect to first allowed route
        const storedRole = localStorage.getItem('userRole');
        if (storedRole && storedRole !== authResponse.user.role) {
          this.clearUserData();
          const firstAllowedRoute = this.getFirstAllowedRoute(authResponse.user.role);
          window.location.href = firstAllowedRoute;
          return authResponse;
        }
        
        this.userSubject.next(authResponse.user);
        this.accessDeniedSubject.next(false);
        localStorage.setItem('userRole', authResponse.user.role);
        localStorage.setItem('userId', authResponse.user.id.toString());
      } else {
        this.userSubject.next(null);
        this.clearUserData();
      }
      this.loadingSubject.next(false);
      return authResponse;
    } catch (error: any) {
      this.loadingSubject.next(false);
      this.userSubject.next(null);
      this.clearUserData();
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        this.accessDeniedSubject.next(true);
      }
      
      return { 
        message: 'Backend validation failed', 
        error: 'NetworkError', 
        cid: error.response?.data?.cid || 'unknown' 
      };
    }
  }

  /**
   * Gets currently authenticated user
   * @returns {User | null} Current user object or null if not authenticated
   */
  getCurrentUser(): User | null {
    return this.userSubject.value;
  }

  /**
   * Gets current user's role
   * @returns {UserRole | null} User role or null if not authenticated
   */
  getUserRole(): UserRole | null {
    const user = this.getCurrentUser();
    return user?.role || null;
  }

  /**
   * Checks if user's role has been updated on backend
   * Called on 403 errors to detect role changes
   * @returns {Promise<void>}
   */
  async checkRoleUpdate(): Promise<void> {
    try {
      // validateUserWithBackend already handles role change detection and redirect
    } catch (error) {
      // Silently handle errors in background role check
    }
  }

  /**
   * Gets the first allowed route for a user based on their role
   * @private
   * @param {UserRole} role - User role
   * @returns {string} First allowed route for the role
   */
  private getFirstAllowedRoute(role: UserRole): string {
    const roleRoutes: Record<UserRole, string> = {
      'Administrator': '/pricing-regional',
      'Manager': '/pricing-regional',
      'Marketer': '/pricing-regional',
      'User': '/pricing-regional'
    };
    return roleRoutes[role] || '/pricing-regional';
  }

  /**
   * Supports exact route matching and parent route matching
   * @param {string} routePath - Route path to check access for
   * @returns {boolean} True if user can access route, false otherwise
   */
  canAccessRoute(routePath: string): boolean {
    const role = this.getUserRole();
    if (!role) return false;

    const routeRoleMap: Record<string, UserRole[]> = {
      '/pricing-regional': ['Administrator', 'Manager', 'Marketer', 'User'],
      '/pricing-province': ['Administrator', 'Manager', 'Marketer', 'User'],
      '/price-comparison': ['Administrator', 'Manager', 'Marketer'],
      '/azk-binding': ['Administrator', 'Manager', 'Marketer', 'User'],
      '/azk-list': ['Administrator', 'Manager', 'Marketer', 'User'],
      '/upload': ['Administrator', 'Manager', 'Marketer'],
      '/price-processing': ['Administrator', 'Manager', 'Marketer'],
      '/scheduler': ['Administrator', 'Manager'],
      '/orders': ['Administrator', 'Manager', 'Marketer'],
      '/brands': ['Administrator', 'Manager', 'Marketer', 'User'],
      '/user-management': ['Administrator']
    };

    let allowedRoles = routeRoleMap[routePath];
    
    if (!allowedRoles) {
      for (const [route, roles] of Object.entries(routeRoleMap)) {
        if (routePath.startsWith(route + '/')) {
          allowedRoles = roles;
          break;
        }
      }
    }
    
    return allowedRoles ? allowedRoles.includes(role) : false;
  }

  /**
   * Clears user data from localStorage
   * @private
   */
  private clearUserData(): void {
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
  }

  /**
   * Logs out user and redirects to Azure AD login
   * Clears all user data, tokens, and storage
   */
  logout(): void {
    this.userSubject.next(null);
    this.authenticatedSubject.next(false);
    this.accessDeniedSubject.next(false);
    this.clearUserData();

    const account = msalInstance.getAllAccounts()[0];
    const logoutHint = account?.idTokenClaims?.login_hint as string | undefined;

    msalInstance.logoutRedirect({
      account,
      logoutHint,
      postLogoutRedirectUri: environment.azure.redirectUri
    });
  }

  /**
   * Checks if JWT token is expired
   * @private
   * @param {AccountInfo} account - MSAL account object
   * @returns {boolean} True if token is expired, false otherwise
   */
  private isTokenExpired(account: AccountInfo): boolean {
    try {
      if (account.idToken) {
        const idTokenPayload = this.decodeJWT(account.idToken);
        if (idTokenPayload && idTokenPayload.exp) {
          const currentTime = Math.floor(Date.now() / 1000);
          const expirationTime = idTokenPayload.exp;
          return currentTime >= expirationTime;
        }
      }
      return true;
    } catch (error) {
      return true;
    }
  }

  /**
   * Decodes JWT token payload
   * @private
   * @param {string} token - JWT token to decode
   * @returns {any} Decoded token payload or null if decoding fails
   */
  private decodeJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  }

  /**
   * Forces logout without redirect (used internally)
   * Clears all data and storage
   * @private
   */
  private forceLogout(): void {
    this.userSubject.next(null);
    this.authenticatedSubject.next(false);
    this.accessDeniedSubject.next(false);
    this.clearUserData();

    msalInstance.logoutRedirect({
      postLogoutRedirectUri: environment.azure.redirectUri
    });
  }

  /**
   * Clears all browser storage (localStorage, sessionStorage, MSAL cache)
   * @private
   */
  private clearAllStorage(): void {
    try {
      msalInstance.clearCache();
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      // Error clearing storage
    }
  }
}

export const authService = new AuthService();
